import {
  Badge,
  Box,
  Collapse,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  Toolbar,
  Typography,
} from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WorkIcon from '@mui/icons-material/Work';
import PeopleIcon from '@mui/icons-material/People';
import PersonIcon from '@mui/icons-material/Person';
import DashboardIcon from '@mui/icons-material/Dashboard';
import NotificationsIcon from '@mui/icons-material/Notifications';
import AssignmentIcon from '@mui/icons-material/Assignment';
import DescriptionIcon from '@mui/icons-material/Description';
import EuroIcon from '@mui/icons-material/Euro';
import AssessmentIcon from '@mui/icons-material/Assessment';
import CampaignIcon from '@mui/icons-material/Campaign';
import HistoryIcon from '@mui/icons-material/History';
import BuildIcon from '@mui/icons-material/Build';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import SettingsIcon from '@mui/icons-material/Settings';
import ScheduleIcon from '@mui/icons-material/Schedule';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import StorefrontIcon from '@mui/icons-material/Storefront';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import DateRangeIcon from '@mui/icons-material/DateRange';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ReceiptIcon from '@mui/icons-material/Receipt';
import SearchIcon from '@mui/icons-material/Search';
import EngineeringIcon from '@mui/icons-material/Engineering';
import CategoryIcon from '@mui/icons-material/Category';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import BusinessIcon from '@mui/icons-material/Business';
import { useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useNotifications } from '../context/NotificationContext';
import { useFeatureFlags } from '../hooks/useFeatureFlags';
import { useShellShortcuts } from '../context/ShellShortcutsContext';
import { deliveryNotesApi } from '../api/deliveryNotes';

interface NavItemLink {
  label: string;
  icon: React.ReactNode;
  path: string;
}

type NavGroupChild = { label: string; path: string };

/** Chiave stabile per stato aperto / icone — non usare la label visibile */
const NAV_GROUP_KEY = {
  finanza: 'finanza',
  ddt: 'ddt',
  cantieri: 'cantieri',
  presenze: 'presenze',
  clienti: 'clienti',
  risorse: 'risorse',
  sistema: 'sistema',
} as const;

type NavGroupKey = (typeof NAV_GROUP_KEY)[keyof typeof NAV_GROUP_KEY];

interface NavItemGroup {
  key: NavGroupKey;
  label: string;
  icon: React.ReactNode;
  children: NavGroupChild[];
}

type NavItem = NavItemLink | NavItemGroup;

function isNavGroup(item: NavItem): item is NavItemGroup {
  return 'children' in item && Array.isArray((item as NavItemGroup).children) && 'key' in item;
}

const linkSx = {
  borderRadius: 2,
  mx: 1,
  mb: 0.5,
  '&.active': {
    bgcolor: 'primary.main',
    color: 'white',
    '& .MuiListItemIcon-root': { color: 'white' },
  },
};

const childLinkSx = {
  ...linkSx,
  pl: 5,
  py: 0.75,
  borderLeft: '2px solid',
  borderColor: 'divider',
  ml: 2,
  mr: 1,
  mb: 0.5,
};

const navItemTextSx = {
  fontSize: 14,
  lineHeight: 1.4,
} as const;

function navLinkEndForPath(groupKey: NavGroupKey, path: string): boolean {
  if (groupKey === NAV_GROUP_KEY.presenze) {
    return path === '/app/attendance';
  }
  if (groupKey === NAV_GROUP_KEY.ddt) {
    // `end` true: con false, /app/delivery-notes resterebbe attivo anche su
    // /app/delivery-notes/pending-assignment (match per prefisso in React Router 6).
    return true;
  }
  if (groupKey === NAV_GROUP_KEY.finanza) {
    return true;
  }
  if (groupKey === NAV_GROUP_KEY.cantieri) {
    return !(
      path === '/app/worksites' ||
      path === '/app/reports' ||
      path === '/app/documents' ||
      path === '/app/invoices' ||
      path === '/app/planning' ||
      path === '/app/integrations'
    );
  }
  if (groupKey === NAV_GROUP_KEY.risorse || groupKey === NAV_GROUP_KEY.sistema || groupKey === NAV_GROUP_KEY.clienti) {
    return true;
  }
  return true;
}

export function AppNavDrawer() {
  const { user } = useAuth();
  const location = useLocation();
  const { openGlobalSearch } = useShellShortcuts();
  const [pendingDnCount, setPendingDnCount] = useState<number | null>(null);
  const {
    hasFinanceAlerts,
    hasPresence,
    hasDeliveryNotes,
    hasSmartMatching: hasDeliveryNoteSmartMatching,
    hasIntegrations,
    hasPlanning,
  } = useFeatureFlags();
  const { unreadCount } = useNotifications();

  const isOperatore = user?.role === 'OPERATORE';
  const isAdminLike = ['AMMINISTRATORE_AZIENDA', 'RESPONSABILE'].includes(user?.role ?? '');

  const finanzaPathActive = useMemo(
    () =>
      location.pathname.startsWith('/app/finance') ||
      (location.pathname.startsWith('/app/analytics') && !location.pathname.startsWith('/app/analytics/procurement')),
    [location.pathname],
  );

  const ddtPathActive = useMemo(
    () =>
      location.pathname.startsWith('/app/suppliers') ||
      location.pathname.startsWith('/app/delivery-notes') ||
      location.pathname.startsWith('/app/analytics/procurement'),
    [location.pathname],
  );

  const presenzePathActive = location.pathname.startsWith('/app/attendance');

  const cantieriPathActive = useMemo(
    () =>
      location.pathname.startsWith('/app/worksites') ||
      location.pathname.startsWith('/app/reports') ||
      location.pathname.startsWith('/app/documents') ||
      location.pathname.startsWith('/app/invoices') ||
      location.pathname.startsWith('/app/planning') ||
      location.pathname.startsWith('/app/integrations'),
    [location.pathname],
  );

  const clientiPathActive =
    location.pathname.startsWith('/app/customers') || location.pathname.startsWith('/app/reminders');

  const risorsePathActive =
    location.pathname.startsWith('/app/materials') ||
    location.pathname.startsWith('/app/equipment') ||
    location.pathname.startsWith('/app/supplier-material-mappings') ||
    location.pathname.startsWith('/app/config/warehouse');

  const sistemaPathActive =
    location.pathname.startsWith('/app/notifications') ||
    location.pathname.startsWith('/app/users') ||
    location.pathname.startsWith('/app/audit') ||
    location.pathname.startsWith('/app/settings/company') ||
    location.pathname.startsWith('/app/settings/worker-qualifications') ||
    location.pathname.startsWith('/app/settings/cost-disciplines') ||
    location.pathname.startsWith('/app/settings/finance-economics');

  const [presenzeOpen, setPresenzeOpen] = useState(presenzePathActive);
  const [finanzaOpen, setFinanzaOpen] = useState(finanzaPathActive);
  const [ddtOpen, setDdtOpen] = useState(ddtPathActive);
  const [cantieriOpen, setCantieriOpen] = useState(cantieriPathActive);
  const [clientiOpen, setClientiOpen] = useState(clientiPathActive);
  const [risorseOpen, setRisorseOpen] = useState(risorsePathActive);
  const [sistemaOpen, setSistemaOpen] = useState(sistemaPathActive);

  useEffect(() => {
    if (presenzePathActive) setPresenzeOpen(true);
  }, [presenzePathActive]);
  useEffect(() => {
    if (finanzaPathActive) setFinanzaOpen(true);
  }, [finanzaPathActive]);
  useEffect(() => {
    if (ddtPathActive) setDdtOpen(true);
  }, [ddtPathActive]);
  useEffect(() => {
    if (cantieriPathActive) setCantieriOpen(true);
  }, [cantieriPathActive]);
  useEffect(() => {
    if (clientiPathActive) setClientiOpen(true);
  }, [clientiPathActive]);
  useEffect(() => {
    if (risorsePathActive) setRisorseOpen(true);
  }, [risorsePathActive]);
  useEffect(() => {
    if (sistemaPathActive) setSistemaOpen(true);
  }, [sistemaPathActive]);

  useEffect(() => {
    if (!hasDeliveryNotes || isOperatore) {
      setPendingDnCount(null);
      return;
    }
    deliveryNotesApi
      .countPendingAssignment()
      .then((r) => setPendingDnCount(r.count))
      .catch(() => setPendingDnCount(null));
  }, [hasDeliveryNotes, isOperatore, location.pathname]);

  const presenzeChildIcons: Record<string, React.ReactNode> = {
    '/app/attendance': <AccessTimeIcon fontSize="small" />,
    '/app/attendance/worksite': <WorkIcon fontSize="small" />,
  };

  const finanzaChildIcons: Record<string, React.ReactNode> = {
    '/app/finance/dashboard': <AssessmentIcon fontSize="small" />,
    '/app/finance/user-costs': <EuroIcon fontSize="small" />,
    '/app/finance/aging': <DateRangeIcon fontSize="small" />,
    '/app/analytics': <AssessmentIcon fontSize="small" />,
  };

  const ddtChildIcons: Record<string, React.ReactNode> = {
    '/app/delivery-notes': <ReceiptLongIcon fontSize="small" />,
    '/app/delivery-notes/pending-assignment': <PendingActionsIcon fontSize="small" />,
    '/app/suppliers': <StorefrontIcon fontSize="small" />,
    '/app/analytics/procurement': <LocalShippingIcon fontSize="small" />,
  };

  const cantieriChildIcons: Record<string, React.ReactNode> = {
    '/app/worksites': <WorkIcon fontSize="small" />,
    '/app/reports': <AssignmentIcon fontSize="small" />,
    '/app/documents': <DescriptionIcon fontSize="small" />,
    '/app/invoices': <ReceiptIcon fontSize="small" />,
    '/app/integrations': <SyncAltIcon fontSize="small" />,
    '/app/planning': <CalendarMonthIcon fontSize="small" />,
  };

  const clientiChildIcons: Record<string, React.ReactNode> = {
    '/app/customers': <PersonIcon fontSize="small" />,
    '/app/reminders/templates': <CampaignIcon fontSize="small" />,
  };

  const risorseChildIcons: Record<string, React.ReactNode> = {
    '/app/equipment': <BuildIcon fontSize="small" />,
    '/app/materials': <Inventory2Icon fontSize="small" />,
    '/app/materials/drafts': <Inventory2Icon fontSize="small" />,
    '/app/supplier-material-mappings': <Inventory2Icon fontSize="small" />,
    '/app/config/warehouse': <SettingsIcon fontSize="small" />,
  };

  const sistemaChildIcons: Record<string, React.ReactNode> = {
    '/app/notifications': <NotificationsIcon fontSize="small" />,
    '/app/users': <PeopleIcon fontSize="small" />,
    '/app/audit': <HistoryIcon fontSize="small" />,
    '/app/settings/worker-qualifications': <EngineeringIcon fontSize="small" />,
    '/app/settings/cost-disciplines': <CategoryIcon fontSize="small" />,
    '/app/settings/finance-economics': <AccountBalanceIcon fontSize="small" />,
    '/app/settings/company': <BusinessIcon fontSize="small" />,
  };

  const groupChildIcons: Record<NavGroupKey, Record<string, React.ReactNode>> = {
    [NAV_GROUP_KEY.finanza]: finanzaChildIcons,
    [NAV_GROUP_KEY.ddt]: ddtChildIcons,
    [NAV_GROUP_KEY.cantieri]: cantieriChildIcons,
    [NAV_GROUP_KEY.presenze]: presenzeChildIcons,
    [NAV_GROUP_KEY.clienti]: clientiChildIcons,
    [NAV_GROUP_KEY.risorse]: risorseChildIcons,
    [NAV_GROUP_KEY.sistema]: sistemaChildIcons,
  };

  const openSetter: Record<NavGroupKey, React.Dispatch<React.SetStateAction<boolean>>> = {
    [NAV_GROUP_KEY.finanza]: setFinanzaOpen,
    [NAV_GROUP_KEY.ddt]: setDdtOpen,
    [NAV_GROUP_KEY.cantieri]: setCantieriOpen,
    [NAV_GROUP_KEY.presenze]: setPresenzeOpen,
    [NAV_GROUP_KEY.clienti]: setClientiOpen,
    [NAV_GROUP_KEY.risorse]: setRisorseOpen,
    [NAV_GROUP_KEY.sistema]: setSistemaOpen,
  };

  const openState: Record<NavGroupKey, boolean> = {
    [NAV_GROUP_KEY.finanza]: finanzaOpen,
    [NAV_GROUP_KEY.ddt]: ddtOpen,
    [NAV_GROUP_KEY.cantieri]: cantieriOpen,
    [NAV_GROUP_KEY.presenze]: presenzeOpen,
    [NAV_GROUP_KEY.clienti]: clientiOpen,
    [NAV_GROUP_KEY.risorse]: risorseOpen,
    [NAV_GROUP_KEY.sistema]: sistemaOpen,
  };

  const navItems: NavItem[] = isOperatore
    ? [
        { label: 'Commesse', icon: <WorkIcon />, path: '/app/worksites' },
        { label: 'Affidi attrezzature', icon: <BuildIcon />, path: '/app/equipment/my-custody' },
        ...(hasPlanning ? [{ label: 'Planning', icon: <CalendarMonthIcon />, path: '/app/planning' }] : []),
        {
          label: 'Notifiche',
          icon: (
            <Badge badgeContent={unreadCount > 0 ? unreadCount : undefined} color="error" max={99}>
              <NotificationsIcon />
            </Badge>
          ),
          path: '/app/notifications',
        },
      ]
    : [
        { label: 'Dashboard', icon: <DashboardIcon />, path: '/app/dashboard' },
        ...(hasFinanceAlerts
          ? [
              {
                key: NAV_GROUP_KEY.finanza,
                label: 'Finanza',
                icon: <AssessmentIcon />,
                children: [
                  { label: 'Dashboard economica', path: '/app/finance/dashboard' },
                  { label: 'Risorse', path: '/app/finance/user-costs' },
                  { label: 'Aging crediti', path: '/app/finance/aging' },
                  { label: 'Analytics', path: '/app/analytics' },
                ],
              } satisfies NavItemGroup,
            ]
          : []),
        ...(hasDeliveryNotes
          ? [
              {
                key: NAV_GROUP_KEY.ddt,
                label: 'DDT & Fornitori',
                icon: <ReceiptLongIcon />,
                children: [
                  { label: 'Tutte le bolle', path: '/app/delivery-notes' },
                  {
                    label:
                      pendingDnCount != null && pendingDnCount > 0
                        ? `Bolle in attesa (${pendingDnCount})`
                        : 'Bolle in attesa',
                    path: '/app/delivery-notes/pending-assignment',
                  },
                  { label: 'Fornitori', path: '/app/suppliers' },
                  { label: 'Acquisti', path: '/app/analytics/procurement' },
                ],
              } satisfies NavItemGroup,
            ]
          : []),
        {
          key: NAV_GROUP_KEY.cantieri,
          label: 'Cantieri & Lavori',
          icon: <WorkIcon />,
          children: [
            { label: 'Commesse', path: '/app/worksites' },
            { label: 'Rapportini', path: '/app/reports' },
            ...(hasPlanning ? [{ label: 'Planning', path: '/app/planning' }] : []),
            ...(hasIntegrations ? [{ label: 'Integrazioni', path: '/app/integrations' }] : []),
            { label: 'Documenti', path: '/app/documents' },
            ...(isAdminLike ? [{ label: 'Fatture', path: '/app/invoices' }] : []),
          ],
        },
        ...(hasPresence
          ? [
              {
                key: NAV_GROUP_KEY.presenze,
                label: 'Presenze',
                icon: <ScheduleIcon />,
                children: [
                  { label: 'Timbratura', path: '/app/attendance' },
                  { label: 'Per commessa', path: '/app/attendance/worksite' },
                ],
              } satisfies NavItemGroup,
            ]
          : [{ label: 'Presenze', icon: <ScheduleIcon />, path: '/app/attendance/worksite' }]),
        {
          key: NAV_GROUP_KEY.clienti,
          label: 'Clienti',
          icon: <PersonIcon />,
          children: [
            { label: 'Clienti', path: '/app/customers' },
            { label: 'Promemoria cliente', path: '/app/reminders/templates' },
          ],
        },
        ...(isAdminLike
          ? [
              {
                key: NAV_GROUP_KEY.risorse,
                label: 'Risorse',
                icon: <Inventory2Icon />,
                children: [
                  { label: 'Attrezzature', path: '/app/equipment' },
                  { label: 'Materiali', path: '/app/materials' },
                  { label: 'Bozze materiali', path: '/app/materials/drafts' },
                  ...(hasDeliveryNoteSmartMatching
                    ? [{ label: 'Mappings fornitore', path: '/app/supplier-material-mappings' }]
                    : []),
                  { label: 'Config. magazzino', path: '/app/config/warehouse' },
                ],
              } satisfies NavItemGroup,
            ]
          : []),
        {
          key: NAV_GROUP_KEY.sistema,
          label: 'Sistema',
          icon: <SettingsIcon />,
          children: [
            {
              label: 'Notifiche',
              path: '/app/notifications',
            },
            ...(isAdminLike ? [{ label: 'Azienda', path: '/app/settings/company' }] : []),
            ...(user?.role === 'AMMINISTRATORE_AZIENDA' ? [{ label: 'Utenti', path: '/app/users' }] : []),
            { label: 'Registro audit', path: '/app/audit' },
            ...(hasFinanceAlerts && user?.role === 'AMMINISTRATORE_AZIENDA'
              ? [
                  { label: 'Qualifiche operai', path: '/app/settings/worker-qualifications' },
                  { label: 'Discipline di costo', path: '/app/settings/cost-disciplines' },
                  { label: 'Impostazioni economiche', path: '/app/settings/finance-economics' },
                ]
              : []),
          ],
        },
      ];

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Toolbar>
        <Typography variant="h6" fontWeight={800} color="primary">
          Cantir
        </Typography>
      </Toolbar>
      <Divider />
      {!isOperatore && isAdminLike && (
        <Box sx={{ px: 1.5, pt: 1.5, pb: 0.5 }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Cerca commesse, clienti, DDT..."
            onClick={() => openGlobalSearch()}
            onFocus={(e) => {
              e.target.blur();
              openGlobalSearch();
            }}
            InputProps={{
              readOnly: true,
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} fontSize="small" />,
            }}
            sx={{
              cursor: 'pointer',
              '& .MuiOutlinedInput-root': { cursor: 'pointer', bgcolor: 'action.hover' },
            }}
          />
        </Box>
      )}
      <List sx={{ flex: 1 }}>
        {navItems.map((item) => {
          if (isNavGroup(item)) {
            const g = item;
            const key = `group-${g.key}`;
            const isOpen = openState[g.key];
            const setOpen = openSetter[g.key];
            const childIcons = groupChildIcons[g.key] ?? {};
            return (
              <Box
                key={key}
                sx={{
                  mx: 1,
                  mb: 0.5,
                  borderRadius: 2,
                  bgcolor: isOpen ? 'action.hover' : 'transparent',
                  transition: 'background-color 0.2s ease',
                }}
              >
                <ListItemButton
                  onClick={() => setOpen((prev) => !prev)}
                  sx={{
                    borderRadius: 2,
                    '&:hover': { bgcolor: isOpen ? 'action.selected' : 'action.hover' },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>{g.icon}</ListItemIcon>
                  <ListItemText
                    primary={g.label}
                    primaryTypographyProps={{
                      variant: 'body2',
                      fontWeight: 600,
                      color: 'text.secondary',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                    }}
                  />
                  {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </ListItemButton>
                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding sx={{ pt: 0, pb: 0.5 }}>
                    {g.children.map((child) => {
                      const end = navLinkEndForPath(g.key, child.path);
                      const icon = childIcons[child.path];
                      const isNotificheSistema = g.key === NAV_GROUP_KEY.sistema && child.path === '/app/notifications';
                      return (
                        <ListItemButton
                          key={child.path}
                          component={NavLink}
                          to={child.path}
                          end={end}
                          sx={childLinkSx}
                        >
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            {isNotificheSistema ? (
                              <Badge
                                badgeContent={unreadCount > 0 ? unreadCount : undefined}
                                color="error"
                                max={99}
                              >
                                {icon}
                              </Badge>
                            ) : (
                              icon
                            )}
                          </ListItemIcon>
                          <ListItemText primary={child.label} primaryTypographyProps={navItemTextSx} />
                        </ListItemButton>
                      );
                    })}
                  </List>
                </Collapse>
              </Box>
            );
          }
          return (
            <ListItemButton key={item.path} component={NavLink} to={item.path} sx={linkSx}>
              <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
              <ListItemText primary={item.label} primaryTypographyProps={navItemTextSx} />
            </ListItemButton>
          );
        })}
      </List>
    </Box>
  );
}
